import { _lcs, locale } from "../../i18n";

/**
 * Shared by the "KI-Kosten" overview (AiCosts) and its per-client detail
 * (AiCostsDetail): labels, colours and number formats for the aiUsage ledger,
 * plus the ECharts options both screens draw with.
 */

// Ledger feature key (core-api VertexAiService.FEATURE) → German source
// string, translated where read. Unknown keys show as they are.
const FEATURE_LABELS = {
    "translation"        : "Übersetzung",
    "concierge"          : "KI-Concierge",
    "concierge-document" : "Concierge: Dokumente lesen",
    "onboarding"         : "Einrichtung bei Registrierung"
};

// One fixed hue per feature, so a feature keeps its colour across charts and
// screens. The brand's four hues, then slate for anything new.
const FEATURE_COLORS = {
    "concierge"          : "#1F4B3F",  /* green-800 */
    "translation"        : "#76A0B2",  /* sky       */
    "concierge-document" : "#6A8A76",  /* green-500 */
    "onboarding"         : "#C2A461"   /* sand-600  */
};
const FALLBACK_COLORS = [ "#B7C2C6", "#8E9DA3", "#DAE0E2" ];

export const PAYER_COLORS = {
    platform : "#C2A461",
    clients  : "#1F4B3F"
};

export const MODEL_COLORS = [ "#1F4B3F", "#76A0B2", "#C2A461", "#6A8A76", "#B7C2C6", "#8E9DA3" ];

export const AiCostFormat = {

    feature( key )
    {
        return FEATURE_LABELS[ key ] ? _lcs( FEATURE_LABELS[ key ] ) : key;
    },

    featureColor( key, index = 0 )
    {
        return FEATURE_COLORS[ key ] || FALLBACK_COLORS[ index % FALLBACK_COLORS.length ];
    },

    /**
     * EUR with two decimals. A month of real usage can stay below one cent,
     * so such amounts read "< 0,01 €" instead of a misleading "0,00 €".
     * `precise` (single calls) keeps four decimals instead.
     */
    money( value, precise = false )
    {
        if ( value === null || value === undefined )
        {
            return "—";
        }
        const amount = Number( value ) || 0;
        const digits = precise ? 4 : 2;
        const format = ( n ) => n.toLocaleString( locale(), {
            style : "currency", currency : "EUR", minimumFractionDigits : digits, maximumFractionDigits : digits
        } );
        const smallest = precise ? 0.0001 : 0.01;
        if ( amount > 0 && amount < smallest )
        {
            return "< " + format( smallest );
        }
        return format( amount );
    },

    /**
     * Axis ticks: as many decimals as the step needs (up to four), so a scale
     * of a few cents reads 0,005 € · 0,01 € · 0,015 € instead of repeating
     * "< 0,01 €".
     */
    axisMoney( value )
    {
        return Number( value || 0 ).toLocaleString( locale(), {
            style : "currency", currency : "EUR", minimumFractionDigits : value === 0 ? 0 : 2, maximumFractionDigits : 4
        } );
    },

    usd( value )
    {
        return Number( value || 0 ).toLocaleString( locale(), {
            style : "currency", currency : "USD", minimumFractionDigits : 2, maximumFractionDigits : 2
        } );
    },

    n( value )
    {
        return Number( value || 0 ).toLocaleString( locale() );
    },

    /** 1234567 → "1,2 Mio." / "1.2M" — token counts. */
    compact( value )
    {
        return Number( value || 0 ).toLocaleString( locale(), { notation : "compact", maximumFractionDigits : 1 } );
    },

    percent( share )
    {
        return Math.round( ( share || 0 ) * 100 ) + " %";
    },

    /** "2026-09" → "September 2026". */
    month( key )
    {
        const date = new Date( key + "-01T00:00:00Z" );
        return isNaN( date.getTime() ) ? key
            : date.toLocaleString( locale(), { month : "long", year : "numeric", timeZone : "UTC" } );
    },

    /** "2026-09" → "Sep. 26" — chart axes. */
    monthShort( key )
    {
        const date = new Date( key + "-01T00:00:00Z" );
        return isNaN( date.getTime() ) ? key
            : date.toLocaleString( locale(), { month : "short", year : "2-digit", timeZone : "UTC" } );
    },

    /** "2026-09-14" → "14.09." */
    day( key )
    {
        const date = new Date( key + "T00:00:00Z" );
        return isNaN( date.getTime() ) ? key
            : date.toLocaleString( locale(), { day : "2-digit", month : "2-digit", timeZone : "UTC" } );
    },

    dateTime( value )
    {
        const date = new Date( value );
        return isNaN( date.getTime() ) ? "—"
            : date.toLocaleString( locale(), { day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit" } );
    },

    duration( ms )
    {
        if ( ms === null || ms === undefined ) return "—";
        return ms < 1000 ? ms + " ms" : ( ms / 1000 ).toLocaleString( locale(), { maximumFractionDigits : 1 } ) + " s";
    },

    /**
     * What the current month will probably cost: spent so far, extrapolated
     * linearly over the days of the month (UTC, like the ledger's months).
     */
    forecast( spent, now = new Date() )
    {
        const daysInMonth = new Date( Date.UTC( now.getUTCFullYear(), now.getUTCMonth() + 1, 0 ) ).getUTCDate();
        const elapsed = ( now.getUTCDate() - 1 ) + ( now.getUTCHours() * 60 + now.getUTCMinutes() ) / 1440;
        return elapsed > 0 ? spent / elapsed * daysInMonth : spent;
    },

    esc( value )
    {
        return String( value === null || value === undefined ? "" : value )
            .replace( /&/g, "&amp;" ).replace( /</g, "&lt;" ).replace( />/g, "&gt;" ).replace( /"/g, "&quot;" );
    },

    /* ------------------------------- ECharts -------------------------------- */

    _base()
    {
        return {
            textStyle : { fontFamily : "Inter, sans-serif", color : "#586A72" },
            tooltip : {
                backgroundColor : "#15332B",
                borderWidth : 0,
                textStyle : { color : "#F2E9DC", fontSize : 12 }
            },
            legend : {
                // One scrollable line: a wrapping legend runs into the axis on a phone.
                type : "scroll", bottom : 0, itemWidth : 10, itemHeight : 10, icon : "circle",
                textStyle : { fontSize : 12, color : "#586A72" }
            }
        };
    },

    _valueAxis()
    {
        return {
            type : "value",
            splitLine : { lineStyle : { color : "rgba(31, 75, 63, 0.10)" } },
            axisLabel : { fontSize : 11, color : "#74858C", formatter : ( v ) => AiCostFormat.axisMoney( v ) }
        };
    },

    _categoryAxis( data )
    {
        return {
            type : "category",
            data : data,
            axisTick : { show : false },
            axisLine : { lineStyle : { color : "rgba(31, 75, 63, 0.18)" } },
            axisLabel : { fontSize : 11, color : "#41525A" }
        };
    },

    /**
     * Stacked monthly columns. `series` = [ { name, color, values[] } ], one
     * value per month. The tooltip lists every non-zero part and the total.
     */
    monthlyStackOption( months, series )
    {
        const base = AiCostFormat._base();
        return {
            ...base,
            grid : { left : 4, right : 8, top : 12, bottom : 52 },
            tooltip : {
                ...base.tooltip,
                trigger : "axis",
                axisPointer : { type : "shadow" },
                formatter : ( params ) =>
                {
                    const parts = params.filter( ( p ) => +p.value > 0 );
                    const total = params.reduce( ( sum, p ) => sum + ( +p.value || 0 ), 0 );
                    return '<strong>' + AiCostFormat.esc( AiCostFormat.month( months[ params[ 0 ].dataIndex ] ) ) + '</strong><br>' +
                        parts.map( ( p ) => p.marker + AiCostFormat.esc( p.seriesName ) + ': ' + AiCostFormat.money( p.value ) ).join( '<br>' ) +
                        ( parts.length > 1 ? '<br><strong>' + AiCostFormat.esc( _lcs( "Gesamt" ) ) + ': ' + AiCostFormat.money( total ) + '</strong>' : '' ) +
                        ( parts.length === 0 ? AiCostFormat.money( 0 ) : '' );
                }
            },
            xAxis : AiCostFormat._categoryAxis( months.map( ( m ) => AiCostFormat.monthShort( m ) ) ),
            yAxis : AiCostFormat._valueAxis(),
            series : series.map( ( s ) => ( {
                name : s.name,
                type : "bar",
                stack : "total",
                barMaxWidth : 28,
                itemStyle : { color : s.color },
                emphasis : { focus : "series" },
                data : s.values
            } ) )
        };
    },

    /** Daily cost line with a dotted 7-day average. */
    dailyOption( days )
    {
        const base = AiCostFormat._base();
        const values = days.map( ( d ) => d.costEur );
        const average = values.map( ( v, i ) =>
        {
            const window = values.slice( Math.max( 0, i - 6 ), i + 1 );
            return window.reduce( ( a, b ) => a + b, 0 ) / window.length;
        } );

        return {
            ...base,
            grid : { left : 4, right : 12, top : 12, bottom : 52 },
            tooltip : {
                ...base.tooltip,
                trigger : "axis",
                formatter : ( params ) =>
                {
                    const day = days[ params[ 0 ].dataIndex ];
                    return '<strong>' + AiCostFormat.esc( AiCostFormat.day( day.day ) ) + '</strong><br>' +
                        AiCostFormat.money( day.costEur ) + ' · ' +
                        AiCostFormat.esc( _lcs( "{count} Anfragen", { count : day.requests, n : AiCostFormat.n( day.requests ) } ) ) + '<br>' +
                        AiCostFormat.esc( _lcs( "7-Tage-Schnitt" ) ) + ': ' + AiCostFormat.money( average[ params[ 0 ].dataIndex ] );
                }
            },
            xAxis : { ...AiCostFormat._categoryAxis( days.map( ( d ) => AiCostFormat.day( d.day ) ) ), boundaryGap : false },
            yAxis : AiCostFormat._valueAxis(),
            series : [
                {
                    name : _lcs( "Kosten pro Tag" ),
                    type : "line",
                    data : values,
                    symbol : "none",
                    lineStyle : { width : 2, color : "#1F4B3F" },
                    itemStyle : { color : "#1F4B3F" },
                    areaStyle : { color : "rgba(31, 75, 63, 0.10)" }
                },
                {
                    name : _lcs( "7-Tage-Schnitt" ),
                    type : "line",
                    data : average,
                    symbol : "none",
                    lineStyle : { width : 1.5, type : "dashed", color : "#C2A461" },
                    itemStyle : { color : "#C2A461" }
                }
            ]
        };
    },

    /** Donut of `items` = [ { name, value, color } ] with the total in the middle. */
    donutOption( items, centerLabel )
    {
        const base = AiCostFormat._base();
        const total = items.reduce( ( sum, i ) => sum + i.value, 0 );
        return {
            ...base,
            tooltip : {
                ...base.tooltip,
                trigger : "item",
                formatter : ( p ) => AiCostFormat.esc( p.name ) + ': ' + AiCostFormat.money( p.value ) + ' (' + p.percent + ' %)'
            },
            series : [ {
                type : "pie",
                radius : [ "52%", "76%" ],
                center : [ "50%", "42%" ],
                itemStyle : { borderColor : "#FFFFFF", borderWidth : 2 },
                label : {
                    show : true,
                    position : "center",
                    formatter : () => AiCostFormat.money( total ) + "\n" + centerLabel,
                    fontSize : 14,
                    fontWeight : 600,
                    color : "#1F4B3F",
                    lineHeight : 20
                },
                emphasis : { label : { show : true }, scale : false },
                labelLine : { show : false },
                data : items.map( ( i ) => ( { name : i.name, value : i.value, itemStyle : { color : i.color } } ) )
            } ]
        };
    }
};
